import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { loginUser, resetAuth } from "../../Store/Actions/authActions";
import Vector from "../../assets/login-page-vector.svg";
import { trackEvent } from "../../helperFunctions";
import { Button } from "../common/Buttons";
import FlexBox from "../common/FlexBox";
import { Body1, Body2, Display } from "../common/Headings";
import Input from "../common/InputBox";
import { GREEN_200, MOSS_GREEN_800, white } from "../common/colors";

const Wrapper = styled(FlexBox)`
  align-items: center;
  height: 100vh;
  overflow: hidden;
  background-color: ${GREEN_200};
`;

const Header = styled(FlexBox)`
  flex-direction: column;
  row-gap: 1rem;
  align-items: center;
`;

const Container = styled(FlexBox)`
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  width: 45rem;
  margin: auto;
  padding: 4rem 6.5rem 0;
  justify-content: space-between;
  background-color: ${white};
  @media screen and (max-width: 768px) {
    padding: 2rem 2rem 0;

    ${Header} {
      ${Display} {
        text-align: center;
      }
    }
  }
`;

const ForgotPasswordLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  align-self: flex-end;
`;

const Logo = styled.img`
  width: 100%;
  max-width: 16.75rem;
  height: 4rem;
  flex-shrink: 0;
`;

const Img = styled.img`
  width: 67%;
  max-width: 30rem;
`;

const Login = () => {
  const { user, loading, error } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    redirectToDashboard();
  }, [user]);

  const redirectToDashboard = () => {
    if (!isEmpty(user)) {
      navigate("/", { replace: true });
    } else {
      dispatch(resetAuth());
    }
  };

  const handleLogin = () => {
    dispatch(loginUser({ email, password }));
  };

  return (
    <Wrapper>
      <Container>
        <FlexBox column rowGap="2.5rem" align="center">
          <Logo
            src={
              "https://assets.theinnerhour.com/compressed/Logo_1776245326790_9yz69o9.svg"
            }
            draggable={false}
            alt="amaha professional logo"
          />
          <Header>
            <Display bold>Welcome to Amaha Professional</Display>
            <Body1>Login with the credentials provided to you.</Body1>
          </Header>
          <FlexBox width="100%" padding="0 1rem" column rowGap="1rem">
            <Input
              name="email"
              placeholder="Email Address"
              value={email}
              onChange={value => setEmail(value)}
            />
            <Input
              name="password"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={value => setPassword(value)}
              icon={showPassword ? FiEyeOff : FiEye}
              error={
                !!error &&
                (error.message
                  ? `Login Error: ${
                      error.message.includes("401")
                        ? `Invalid credentials`
                        : error.message
                    }`
                  : "Login Error")
              }
              onIconClick={handleTogglePassword}
              onSubmit={handleLogin}
            />
            <ForgotPasswordLink
              to={`/forgotpassword`}
              onClick={() => trackEvent({ event: "twa_forgot_pswd_click" })}
            >
              <Body2 bold color={MOSS_GREEN_800} id="forgot-password">
                Forgot Password?
              </Body2>
            </ForgotPasswordLink>
          </FlexBox>
          <Button
            id="login-button"
            disabled={!/\S/.test(email) || !/\S/.test(password) || loading}
            onClick={handleLogin}
          >
            {loading ? "Loading" : "Login"}
          </Button>
        </FlexBox>
        <Img src={Vector} alt="banner" />
      </Container>
    </Wrapper>
  );
};

export default Login;
